import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, Loader2, AlertCircle, Zap, Shield, Brain, Clock } from 'lucide-react';
import axios from 'axios';
import SandboxChoiceModal from '../components/SandboxChoiceModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Elegant Background Component - matching landing page
const ElegantBackground = () => {
  return (
    <div className="fixed inset-0 overflow-hidden pointer-events-none" style={{ zIndex: 1 }}>
      {/* Primary orb - top right */}
      <motion.div
        className="absolute -top-[20%] -right-[10%] w-[800px] h-[800px] rounded-full"
        style={{
          background: 'radial-gradient(circle, rgba(139, 92, 246, 0.15) 0%, rgba(139, 92, 246, 0.05) 40%, transparent 70%)',
        }}
        animate={{
          x: [0, 30, 0],
          y: [0, 20, 0],
        }}
        transition={{
          duration: 20,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />
      
      {/* Secondary orb - bottom left */}
      <motion.div
        className="absolute -bottom-[30%] -left-[15%] w-[900px] h-[900px] rounded-full"
        style={{
          background: 'radial-gradient(circle, rgba(99, 102, 241, 0.12) 0%, rgba(99, 102, 241, 0.04) 40%, transparent 70%)',
        }}
        animate={{
          x: [0, -20, 0],
          y: [0, -30, 0],
        }}
        transition={{
          duration: 25,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />
      
      {/* Accent orb - center */}
      <motion.div
        className="absolute top-[30%] left-[40%] w-[600px] h-[600px] rounded-full"
        style={{
          background: 'radial-gradient(circle, rgba(168, 85, 247, 0.08) 0%, transparent 60%)',
        }}
        animate={{
          scale: [1, 1.1, 1],
          opacity: [0.5, 0.8, 0.5],
        }}
        transition={{
          duration: 15,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />
    </div>
  );
};

// Translations
const translations = {
  nb: {
    createAccount: 'Opprett konto',
    subtitle: 'Start din gratis prøveperiode i dag',
    connectingGoogle: 'Kobler til Google...',
    continueWithGoogle: 'Fortsett med Google',
    connectingMicrosoft: 'Kobler til Microsoft...',
    continueWithMicrosoft: 'Fortsett med Microsoft',
    or: 'eller',
    registerWithEmail: 'Registrer med e-post',
    fullName: 'Fullt navn',
    fullNamePlaceholder: 'Ola Nordmann',
    email: 'E-post',
    emailPlaceholder: 'ola@advokatfirma.no',
    password: 'Passord',
    passwordPlaceholder: 'Minst 8 tegn',
    phone: 'Telefon (valgfritt)',
    phonePlaceholder: '+47 XXX XX XXX',
    companyName: 'Firmanavn (valgfritt)',
    companyNamePlaceholder: 'Advokatfirmaet AS',
    creating: 'Oppretter konto...',
    createAccountBtn: 'Opprett konto',
    alreadyHaveAccount: 'Har du allerede en konto?',
    login: 'Logg inn',
    errorDefault: 'Kunne ikke opprette konto. Prøv igjen.',
    backToOptions: 'Tilbake til valg',
    backToHome: '← Tilbake til forsiden',
    secureSignup: 'Sikker registrering',
    noCreditCard: 'Ingen kredittkort nødvendig',
  },
  en: {
    createAccount: 'Create account',
    subtitle: 'Start your free trial today',
    connectingGoogle: 'Connecting to Google...',
    continueWithGoogle: 'Continue with Google',
    connectingMicrosoft: 'Connecting to Microsoft...',
    continueWithMicrosoft: 'Continue with Microsoft',
    or: 'or',
    registerWithEmail: 'Register with email',
    fullName: 'Full name',
    fullNamePlaceholder: 'John Doe',
    email: 'Email',
    emailPlaceholder: 'john@lawfirm.com',
    password: 'Password',
    passwordPlaceholder: 'At least 8 characters',
    phone: 'Phone (optional)',
    phonePlaceholder: '+1 XXX XXX XXXX',
    companyName: 'Company name (optional)',
    companyNamePlaceholder: 'Law Firm Inc.',
    creating: 'Creating account...',
    createAccountBtn: 'Create account',
    alreadyHaveAccount: 'Already have an account?',
    login: 'Log in',
    errorDefault: 'Could not create account. Please try again.',
    backToOptions: 'Back to options',
    backToHome: '← Back to homepage',
    secureSignup: 'Secure registration',
    noCreditCard: 'No credit card required',
  }
};

// Microsoft Logo SVG Component
const MicrosoftLogo = () => (
  <svg width="20" height="20" viewBox="0 0 21 21" xmlns="http://www.w3.org/2000/svg">
    <rect x="1" y="1" width="9" height="9" fill="#F25022"/>
    <rect x="11" y="1" width="9" height="9" fill="#7FBA00"/>
    <rect x="1" y="11" width="9" height="9" fill="#00A4EF"/>
    <rect x="11" y="11" width="9" height="9" fill="#FFB900"/>
  </svg>
);

// Google Logo SVG Component
const GoogleLogo = () => (
  <svg width="20" height="20" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
  </svg>
);

interface LexySignupPageProps {
  lang?: 'nb' | 'en';
}

const LexySignupPage: React.FC<LexySignupPageProps> = ({ lang = 'nb' }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const t = translations[lang];
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    company_name: ''
  });
  const [loading, setLoading] = useState(false);
  const [microsoftLoading, setMicrosoftLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError] = useState('');
  const [showSandboxChoice, setShowSandboxChoice] = useState(false);
  const [signupData, setSignupData] = useState(null);
  const [showEmailForm, setShowEmailForm] = useState(false);
  
  // Check for pre-filled data from OAuth
  useEffect(() => {
    const email = searchParams.get('email');
    const name = searchParams.get('name');
    const provider = searchParams.get('provider');
    
    if ((provider === 'microsoft' || provider === 'google') && (email || name)) {
      setTimeout(() => {
        setFormData(prev => ({
          ...prev,
          email: email || prev.email,
          name: name || prev.name
        }));
      }, 0);
    }
  }, [searchParams]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };
  
  const handleMicrosoftSignup = () => {
    setMicrosoftLoading(true);
    window.location.href = `${API}/auth/microsoft/signup`;
  };

  const handleGoogleSignup = () => {
    setGoogleLoading(true);
    window.location.href = `${API}/auth/google/signup`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API}/auth/signup/lexy`, formData);
      
      localStorage.setItem('user', JSON.stringify(response.data.user));
      if (response.data.token) {
        localStorage.setItem('token', response.data.token);
      }
      
      setSignupData(response.data);
      setShowSandboxChoice(true);
      setLoading(false);
      
    } catch (err: any) {
      setError(err.response?.data?.detail || t.errorDefault);
      setLoading(false);
    }
  };

  const handleSandboxChoice = async (choice: string) => {
    if (choice === 'demo') {
      try {
        const response = await axios.post(`${API}/admin/sandbox/create`);
        localStorage.setItem('sandbox_tenant_id', response.data.tenant_id);
        localStorage.setItem('is_sandbox', 'true');
        
        try {
          const loginResponse = await axios.post(`${API}/auth/login`, {
            email: formData.email,
            password: formData.password
          });
          
          if (loginResponse.data.token) {
            localStorage.setItem('token', loginResponse.data.token);
          }
          localStorage.setItem('user', JSON.stringify(loginResponse.data.user));
          
          setShowSandboxChoice(false);
          navigate('/lexy/dashboard', {
            state: {
              sandboxStatus: 'seeding',
              sandboxTenantId: response.data.tenant_id
            }
          });
        } catch (reloginErr) {
          setShowSandboxChoice(false);
          navigate('/lexy/dashboard');
        }
        
      } catch (err) {
        setShowSandboxChoice(false);
        navigate('/lexy/dashboard');
      }
    } else {
      localStorage.removeItem('is_sandbox');
      setShowSandboxChoice(false);
      navigate('/lexy/dashboard');
    }
  };

  const inputClasses = "w-full h-11 px-4 bg-white/5 border border-white/10 rounded-xl text-white text-sm placeholder-white/25 focus:outline-none focus:border-white/25 focus:bg-white/[0.07] transition-all duration-300";

  return (
    <div className="min-h-screen bg-[#08080a] relative overflow-hidden">
      {/* Animated gradient background - matching landing page */}
      <ElegantBackground />
      
      <div className="min-h-screen flex items-center justify-center p-8 relative" style={{ zIndex: 2 }}>
        <div className="w-full max-w-sm">
          {/* Logo - minimalist */}
          <motion.div 
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="flex items-center gap-2.5 mb-12"
          >
            <div className="w-9 h-9 bg-gradient-to-br from-violet-500 to-purple-600 rounded-lg flex items-center justify-center">
              <Zap className="w-4.5 h-4.5 text-white" strokeWidth={2.5} />
            </div>
            <span className="text-xl font-medium text-white tracking-tight">Lexy</span>
          </motion.div>
          
          {/* Header - elegant typography */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <h1 className="text-2xl font-light text-white mb-2 tracking-tight">{t.createAccount}</h1>
            <p className="text-white/40 text-sm mb-10">{t.subtitle}</p>
          </motion.div>

          {!showEmailForm ? (
            <motion.div 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="space-y-3"
            >
              {/* Google Button - minimalist */}
              <button
                onClick={handleGoogleSignup}
                disabled={googleLoading || microsoftLoading}
                className="w-full h-11 flex items-center justify-center gap-3 bg-white/95 text-neutral-700 rounded-xl text-[14px] font-medium hover:bg-white transition-all duration-300 disabled:opacity-50"
              >
                {googleLoading ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> {t.connectingGoogle}</>
                ) : (
                  <><GoogleLogo /> {t.continueWithGoogle}</>
                )}
              </button>

              {/* Microsoft Button - minimalist */}
              <button
                onClick={handleMicrosoftSignup}
                disabled={microsoftLoading || googleLoading}
                className="w-full h-11 flex items-center justify-center gap-3 bg-white/95 text-neutral-700 rounded-xl text-[14px] font-medium hover:bg-white transition-all duration-300 disabled:opacity-50"
              >
                {microsoftLoading ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> {t.connectingMicrosoft}</>
                ) : (
                  <><MicrosoftLogo /> {t.continueWithMicrosoft}</>
                )}
              </button>

              {/* Divider - subtle */}
              <div className="flex items-center gap-4 py-4">
                <div className="flex-1 h-px bg-white/10"></div>
                <span className="text-white/30 text-xs">{t.or}</span>
                <div className="flex-1 h-px bg-white/10"></div>
              </div>

              {/* Email Button - ghost style */}
              <button
                onClick={() => setShowEmailForm(true)}
                className="w-full h-11 flex items-center justify-center gap-2 bg-white/5 border border-white/10 text-white/70 rounded-xl text-[14px] font-medium hover:bg-white/10 hover:text-white/90 hover:border-white/20 transition-all duration-300"
              >
                {t.registerWithEmail}
                <ArrowRight className="w-4 h-4" />
              </button>

              {/* Security note - subtle */}
              <div className="flex items-center justify-center gap-2 pt-6 text-white/30 text-xs">
                <Shield className="w-3.5 h-3.5 text-emerald-500/70" />
                {t.secureSignup}
              </div>
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.4 }}
            >
              {/* Back button - minimal */}
              <button
                onClick={() => setShowEmailForm(false)}
                className="flex items-center gap-1.5 text-white/40 hover:text-white/70 mb-8 text-sm transition-all duration-300"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                {t.backToOptions}
              </button>

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Name */}
                <div>
                  <label className="block text-white/40 text-xs mb-2 tracking-wide">{t.fullName}</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder={t.fullNamePlaceholder}
                    className={inputClasses}
                  />
                </div>

                {/* Email */}
                <div>
                  <label className="block text-white/40 text-xs mb-2 tracking-wide">{t.email}</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder={t.emailPlaceholder}
                    className={inputClasses}
                  />
                </div>

                {/* Password */}
                <div>
                  <label className="block text-white/40 text-xs mb-2 tracking-wide">{t.password}</label>
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    minLength={8}
                    placeholder={t.passwordPlaceholder}
                    className={inputClasses}
                  />
                </div>

                {/* Phone & Company in row */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-white/40 text-xs mb-2 tracking-wide">{t.phone}</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder={t.phonePlaceholder}
                      className={inputClasses}
                    />
                  </div>
                  <div>
                    <label className="block text-white/40 text-xs mb-2 tracking-wide">{t.companyName}</label>
                    <input
                      type="text"
                      name="company_name"
                      value={formData.company_name}
                      onChange={handleChange}
                      placeholder={t.companyNamePlaceholder}
                      className={inputClasses}
                    />
                  </div>
                </div>

                {/* Error */}
                {error && (
                  <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 text-sm">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    {error}
                  </div>
                )}

                {/* Submit */}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full h-11 flex items-center justify-center gap-2 bg-white/10 hover:bg-white/15 border border-white/20 hover:border-white/30 text-white rounded-xl text-sm font-medium transition-all duration-300 disabled:opacity-50 mt-2"
                >
                  {loading ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> {t.creating}</>
                  ) : (
                    <>{t.createAccountBtn} <ArrowRight className="w-4 h-4" /></>
                  )}
                </button>
              </form>
            </motion.div>
          )}

          {/* Login link - subtle */}
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mt-10 text-center"
          >
            <span className="text-white/30 text-sm">{t.alreadyHaveAccount} </span>
            <button
              onClick={() => navigate('/lexy/login')}
              className="text-white/70 hover:text-white text-sm font-medium transition-all duration-300"
            >
              {t.login}
            </button>
          </motion.div>

          {/* Back to home - very subtle */}
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="mt-4 text-center"
          >
            <button
              onClick={() => navigate('/')}
              className="text-white/20 hover:text-white/40 text-xs transition-all duration-300"
            >
              {t.backToHome}
            </button>
          </motion.div>

          {/* No credit card note */}
          <motion.p 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="text-center text-white/20 text-xs mt-6"
          >
            {t.noCreditCard}
          </motion.p>
        </div>
      </div>

      {/* Sandbox Choice Modal */}
      {showSandboxChoice && (
        <SandboxChoiceModal
          onChoice={(choice: string) => handleSandboxChoice(choice)}
        />
      )}
    </div>
  );
};

export default LexySignupPage;
